import { ApiContextValueUnresolved, useApiContext } from '../../ApiContext';
import type { ResponsePayload, RetryOptions } from '../../types';
import type { RequestOptions } from '../types';
import {
  composeQuery,
  composeRequestBody,
  mergeRetryOptions,
  request,
} from '../util';

export const useRequest = (
  apiContextOptions: Partial<ApiContextValueUnresolved> = {},
) => {
  const context = useApiContext(apiContextOptions);

  return <T extends ResponsePayload>(
    urlPath: string,
    requestOptions: RequestOptions & {
      retryOptions?: Partial<RetryOptions>;
    } = {},
  ): Promise<T> => {
    const retryOptions = mergeRetryOptions({
      // Start with retryOptions from context.
      baseRetryOptions: context.defaultRetryOptions,
      // Override with retryOptions from requestOptions, if any.
      retryOptionsToMerge: requestOptions.retryOptions,
    });

    const query = composeQuery(
      requestOptions.query,
      requestOptions.shouldConvertQueryKeysToSnakeCase ?? true,
    );

    const {
      body: bodyFromInit,
      headers: headersFromInit,
      ...restRequestInit
    } = requestOptions.init ?? {};

    const { body, headerOverrides } = composeRequestBody(bodyFromInit);

    const headers = {
      // Start with headers from context.
      ...context.headers,
      // Override with headers from requestOptions.init if any.
      ...headersFromInit,
      // Apply header overrides based on body, if any. Main purpose is to
      // override content type to 'application/json' if we converted the body
      // to JSON.
      ...headerOverrides,
    };

    return request<T>(
      `${context.baseUrl}${urlPath}${query}`,
      {
        // Start with request options from context.
        ...context.defaultRequestOptions,
        // Override with values from requestOptions.init if any.
        ...restRequestInit,
        // Use modified body and headers.
        body,
        headers,
        // Add retryOptions.
        retryOptions,
      },
      {
        shouldConvertResponseKeysToCamelCase:
          requestOptions.shouldConvertResponseKeysToCamelCase ?? true,
      },
    );
  };
};
